<template>
  <article
    v-if="post"
    :class="['travel-blog', { 'travel-blog-has-journey': hasJourney }]"
  >
    <JourneyTimeline
      v-if="hasJourney"
      v-bind="post.journey"
      compact
      class="travel-blog-journey-nav"
    />

    <div class="travel-blog-content">
      <div class="blog-kicker">{{ kicker }}</div>
      <h1 class="blog-title">{{ post.title }}</h1>
      <p class="blog-lede">{{ post.lede }}</p>

      <ResponsiveMediaGallery
        v-if="post.images && post.images.length"
        :items="post.images"
        :grid-min-width="240"
        natural-images
        aria-label="Story gallery"
        class="blog-media-gallery"
      />

      <section
        v-for="section in post.sections"
        :key="section.heading"
        :id="sectionId(section)"
        class="blog-section"
      >
        <h2 class="blog-section-title">{{ section.heading }}</h2>
        <p
          v-if="section.subheading"
          class="blog-section-subheading"
        >
          {{ section.subheading }}
        </p>
        <p
          v-for="paragraph in section.paragraphs || []"
          :key="paragraph"
          class="blog-paragraph"
        >
          {{ paragraph }}
        </p>

        <dl
          v-if="section.rows && section.rows.length"
          class="blog-fact-list"
        >
          <div
            v-for="row in section.rows"
            :key="row.label"
            class="blog-fact-row"
          >
            <dt>
              <router-link
                v-if="row.route"
                :to="row.route"
                class="blog-fact-link"
              >
                {{ row.label }}
              </router-link>
              <span v-else>{{ row.label }}</span>
            </dt>
            <dd>{{ row.value }}</dd>
          </div>
        </dl>

        <ul
          v-if="section.bullets && section.bullets.length"
          class="blog-bullet-list"
        >
          <li
            v-for="bullet in section.bullets"
            :key="bullet"
          >
            {{ bullet }}
          </li>
        </ul>

        <p
          v-for="(callout, calloutIndex) in section.callouts || (section.callout ? [section.callout] : [])"
          :key="`${section.heading}-callout-${calloutIndex}`"
          class="blog-callout"
        >
          <strong v-if="section.calloutLabel && calloutIndex === 0">{{ section.calloutLabel }} </strong>{{ callout }}
        </p>

        <p
          v-if="section.note"
          class="blog-note"
        >
          {{ section.note }}
        </p>

        <p
          v-if="section.emphasis"
          class="blog-emphasis"
        >
          {{ section.emphasis }}
        </p>

        <ul
          v-if="section.links && section.links.length"
          class="blog-link-list"
        >
          <li
            v-for="link in section.links"
            :key="link.label"
          >
            <router-link
              v-if="link.route"
              :to="link.route"
              :class="['blog-text-link', { 'blog-text-link-primary': link.primary }]"
            >
              {{ link.label }}
              <v-icon v-if="link.primary" small right>mdi-arrow-right</v-icon>
            </router-link>
            <a
              v-else
              :href="link.href"
              :class="['blog-text-link', { 'blog-text-link-primary': link.primary }]"
            >
              {{ link.label }}
              <v-icon v-if="link.primary" small right>mdi-arrow-right</v-icon>
            </a>
          </li>
        </ul>

        <div
          v-if="section.subsections && section.subsections.length"
          class="blog-subsections"
        >
          <section
            v-for="subsection in section.subsections"
            :key="subsection.heading"
            :id="sectionId(subsection)"
            class="blog-subsection"
          >
            <h3 class="blog-subsection-title">{{ subsection.heading }}</h3>
            <dl
              v-if="subsection.rows && subsection.rows.length"
              class="blog-fact-list blog-subsection-facts"
            >
              <div
                v-for="row in subsection.rows"
                :key="row.label"
                class="blog-fact-row"
              >
                <dt>
                  <router-link
                    v-if="row.route"
                    :to="row.route"
                    class="blog-fact-link"
                  >
                    {{ row.label }}
                  </router-link>
                  <span v-else>{{ row.label }}</span>
                </dt>
                <dd>{{ row.value }}</dd>
              </div>
            </dl>
            <p
              v-for="paragraph in subsection.paragraphs || []"
              :key="paragraph"
              class="blog-paragraph"
            >
              {{ paragraph }}
            </p>
            <ul
              v-if="subsection.bullets && subsection.bullets.length"
              class="blog-bullet-list"
            >
              <li
                v-for="bullet in subsection.bullets"
                :key="bullet"
              >
                {{ bullet }}
              </li>
            </ul>
            <ResponsiveMediaGallery
              v-if="subsection.images && subsection.images.length"
              :items="subsection.images"
              :grid-min-width="240"
              natural-images
              :aria-label="`${subsection.heading} gallery`"
              class="blog-media-gallery blog-section-media-gallery"
            />
            <p
              v-if="subsection.note"
              class="blog-note"
            >
              {{ subsection.note }}
            </p>
          </section>
        </div>

        <ResponsiveMediaGallery
          v-if="section.images && section.images.length"
          :items="section.images"
          :grid-min-width="240"
          natural-images
          :aria-label="`${section.heading} gallery`"
          class="blog-media-gallery blog-section-media-gallery"
        />
      </section>
    </div>
  </article>
</template>

<script>
import JourneyTimeline from './JourneyTimeline.vue';
import ResponsiveMediaGallery from './reckon-way/media/ResponsiveMediaGallery.vue';

export default {
  name: 'TravelBlogArticle',
  components: {
    JourneyTimeline,
    ResponsiveMediaGallery,
  },
  props: {
    post: {
      type: Object,
      required: true,
    },
    kicker: {
      type: String,
      default: 'Travel Blog',
    },
  },
  computed: {
    hasJourney() {
      const journey = this.post && this.post.journey;

      return Boolean(
        journey
        && (
          (journey.items && journey.items.length)
          || (journey.days && journey.days.length)
        ),
      );
    },
  },
  methods: {
    sectionId(section) {
      return section.id || null;
    },
  },
};
</script>

<style scoped>
.travel-blog {
  max-width: 860px;
}

.travel-blog-has-journey {
  align-items: start;
  --journey-rail-width: 250px;
  display: grid;
  gap: 28px;
  grid-template-columns: var(--journey-rail-width) minmax(0, 860px);
  max-width: 1160px;
}

.travel-blog-journey-nav {
  align-self: start;
  max-height: calc(100vh - 104px);
  overflow: auto;
  position: sticky;
  top: 82px;
  width: 100%;
}

.travel-blog-content {
  grid-column: 2;
  min-width: 0;
}

.blog-kicker {
  color: #e23232;
  font-family: "Oswald", serif;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
}

.blog-title {
  color: #fff;
  font-family: "Oswald", serif;
  font-size: 2.6rem;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.08;
  margin: 10px 0 14px;
}

.blog-lede {
  color: #d5d5d5;
  font-size: 1.25rem;
  line-height: 1.6;
  margin-bottom: 30px;
}

.blog-media-gallery {
  margin: 28px 0 34px;
}

.blog-section-media-gallery {
  margin: 22px 0 4px;
}

.blog-section {
  margin-top: 34px;
}

.blog-section-title {
  border-left: 5px solid #e23232;
  color: #fff;
  font-family: "Oswald", serif;
  font-size: 1.5rem;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.18;
  margin-bottom: 14px;
  padding-left: 16px;
}

.blog-section-subheading {
  color: #fff;
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  line-height: 1.45;
  margin: -4px 0 12px;
  text-transform: uppercase;
}

.blog-paragraph {
  color: #c9c9c9;
  font-size: 1.08rem;
  line-height: 1.75;
  margin-bottom: 16px;
}

.blog-fact-list {
  border: 1px solid #2a2a2a;
  border-radius: 8px;
  margin: 18px 0 4px;
  overflow: hidden;
}

.blog-fact-row {
  display: grid;
  grid-template-columns: minmax(180px, 0.8fr) 1fr;
}

.blog-fact-row + .blog-fact-row {
  border-top: 1px solid #242424;
}

.blog-fact-row dt,
.blog-fact-row dd {
  color: #d8d8d8;
  font-size: 1rem;
  line-height: 1.45;
  margin: 0;
  padding: 14px 16px;
}

.blog-fact-row dt {
  background: #101010;
  color: #fff;
  font-weight: 800;
}

.blog-fact-row dd {
  background: #080808;
}

.blog-fact-link {
  color: #fff !important;
  text-decoration: none;
}

.blog-fact-link:hover,
.blog-fact-link:focus {
  color: #e23232 !important;
}

.blog-bullet-list {
  color: #c9c9c9;
  font-size: 1.06rem;
  line-height: 1.7;
  margin: 4px 0 18px;
  padding-left: 24px;
}

.blog-link-list {
  display: grid;
  gap: 10px;
  list-style: none;
  margin: 12px 0 18px;
  padding: 0;
}

.blog-text-link {
  align-items: center;
  border: 1px solid #2a2a2a;
  border-radius: 8px;
  color: #fff !important;
  display: inline-flex;
  font-family: "Oswald", serif;
  font-size: 1.1rem;
  font-weight: 900;
  letter-spacing: 0;
  min-height: 46px;
  min-width: 220px;
  padding: 10px 14px;
  text-decoration: none;
  transition: border-color 160ms ease, color 160ms ease, transform 160ms ease;
}

.blog-text-link:hover,
.blog-text-link:focus {
  border-color: #e23232;
  color: #e23232 !important;
  transform: translateX(3px);
}

.blog-text-link-primary {
  background: #e23232;
  border-color: #ff6b6b;
  box-shadow: 0 8px 24px rgba(226, 50, 50, 0.32);
  color: #fff !important;
  padding: 12px 18px;
}

.blog-text-link-primary .v-icon {
  color: currentColor;
}

.blog-text-link-primary:hover,
.blog-text-link-primary:focus {
  background: #fff;
  border-color: #fff;
  color: #111 !important;
}

.blog-callout {
  color: #e6e6e6;
  font-size: 1.03rem;
  font-style: italic;
  line-height: 1.65;
  margin: 8px 0 12px;
}

.blog-note {
  color: #e6e6e6;
  font-size: 1rem;
  font-style: italic;
  line-height: 1.5;
  margin: 12px 0 0;
}

.blog-emphasis {
  color: #fff;
  font-size: 1.08rem;
  font-weight: 900;
  line-height: 1.55;
  margin: 20px 0 0;
}

.blog-subsections {
  display: grid;
  gap: 20px;
  margin-top: 18px;
}

.blog-subsection {
  border-left: 2px solid #2a2a2a;
  padding-left: 18px;
}

.blog-subsection-title {
  color: #fff;
  font-family: "Oswald", serif;
  font-size: 1.18rem;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.2;
  margin: 0 0 10px;
}

@media (max-width: 600px) {
  .blog-title {
    font-size: 2rem;
  }

  .blog-lede {
    font-size: 1.1rem;
  }

  .blog-image-grid {
    grid-template-columns: 1fr;
  }

  .blog-fact-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 1040px) {
  .travel-blog-has-journey {
    display: block;
    max-width: 860px;
  }

  .travel-blog-journey-nav {
    max-height: none;
    position: static;
    width: auto;
  }
}
</style>
